import LevelManager from "./level-manager.js";
import LoaderGF4 from "./gf4/loader-gf4";

/**
 * @module Loader
 */
const Loader = {
    /**
     * Return a `preload` function to use in game boot.
     * @param {string | object} data - base game data.
     *  In string form, this points at a game file.
     *  In object literal form, this is JSON-style compiled data.
     * @static
     * @returns {Function}
     */
    getPreload: function ( data, splash ) {
        return function () {
            this.load.crossOrigin = "anonymous";

            // Get base game data.
            // This will exist as a mutable JSON-style object hereafter.
            if ( typeof data === "string" ) {
                this.load.json( "game", data );
            } else {
                this.game.cache.json.add( "game", data );
            }

            // Get splash logo.
            if ( !splash ) {
                splash = {};
            }
            if ( typeof splash.image !== "string" ) {
                splash.image = "assets/gamefrootAtlas.png";
            }
            if (typeof splash.json !== "string" ) {
                splash.json = "assets/gamefrootAtlas.phaser.json";
            }
            this.load.atlas(
                "gamefroot-splash-screen",
                splash.image,
                splash.json );
        };
    },

    /**
     * Return a `create` function to use in game boot.
     * This is necessary because `create` runs in the context of a Scene,
     * so we must curry in the `Loader`.
     * @static
     * @returns {Function} 
     */
    getCreate: function () {  
        const Loader = this;
        return function () {
            // Bring up loading graphics.
            Loader.startIndicators( this );

            // Apply post-boot config options.
            const gameJson = this.cache.json.get( "game" );

            // Identify format.
            let formatVersion = null;
            if (gameJson && gameJson.formatVersion ) {
                formatVersion = gameJson.formatVersion;
            }
            if (
                typeof formatVersion === "string" &&
                formatVersion.match(/^gamefroot-4\./))
            {
                // This is a version 4 ResourceStore
                // (see our repo gamefroot-editor-4).
                LoaderGF4.doCreate( gameJson, this, Loader );
            } else {
                // This is not gamefroot-editor-4 compatible.
                // We may expect this during a brief window of changeover.
                // Alert the user to hold on - big things are coming.
                this.game.reportError("Gamefroot Version 4 is rolling out! Get ready for a powerful new experience.\n\nYour project needs to be a Gamefroot Version 4 file in order to play. Please save your work and reload Gamefroot to get the newest version. It will be available in the next few minutes. Thank you for your patience.");
            }
        }
    },

    /**
     * Start loading any queued files.
     * By using Promises, we gain automatic deferral on subsequent code.
     * This is important, because you don't want to load within a load process.
     * In my experience, the second load will just not happen.
     * @param {Phaser.Scene} scene - game level
     * @returns {Promise}
     */
    loadAndRun: function ( scene ) {
        return new Promise( ( resolve, reject ) => {
            if ( scene.load.list.size > 0 ) {
                scene.load.once( "complete", resolve );
                scene.load.once( "error", reject );
                scene.load.start();
            } else {
                resolve();
            }
        } );
    },

    /**
     * Create and initialize load indicators (Gamefroot logo and loading bar).
     * Begin fading in.
     * @param {Phaser.Scene} scene - game level
     */
    startIndicators: function ( scene ) {
        // Create game objects.
        const gamefrootLogo = scene.add.image(
            scene.game.config.width / 2,
            scene.game.config.height / 2,
            "gamefroot-splash-screen",
            "gamefroot-logo" );
        const loadingBar = scene.add.image(
            0,
            scene.game.config.height,
            "gamefroot-splash-screen",
            "loading-bar",
        );
        loadingBar.setOrigin( 0, 1 );
        loadingBar.scaleX = 0;
        loadingBar.scaleFactor = scene.game.config.width / loadingBar.width;

        scene.data.set( "loadingBar", loadingBar );
        scene.data.set( "cameraFadedIn", false );
        scene.data.set( "loadCompleted", false );

        // Fade in and out.
        scene.cameras.main.fadeFrom( 500, 0, 0, 0, false, ( _cam, k ) => {
            if ( k >= 1 ) {
                // Try to ensure the game is properly scaled.
                game.scale.refresh();

                setTimeout( () => {
                    scene.data.set( "cameraFadedIn", true );
                    if ( scene.data.get( "loadCompleted" ) ) {
                        this.finishIndicators( scene );
                    }
                }, 1000 )
            }
        } );
    },

    /**
     * Fade out and transition to the first proper game level.
     * @param {Phaser.Scene} scene - game level
     */
    finishIndicators: function ( scene ) {
        scene.cameras.main.fadeOut( 500, 0, 0, 0, ( _cam, k ) => {
            if ( k >= 1 ) {
                // Try to ensure the game is properly scaled.
                game.scale.refresh();

                // Switch to the first level.
                // NOTE: Although there is a "firstLevel"
                // value in `game.json`,
                // it is hard coded to 1 in the compiler.
                // Thus, we always go to level 1 first,
                // so we might as well do it by default.
                LevelManager.switchTo( 1 );
            }
        } );
    },

    /**
     * Update the progress bar based on the current loading stage.
     * @param {Phaser.Scene} scene - game level
     * @param {Number} progress - progress of the current loading batch
     * @param {Number} size - normalized length of current loading bar segment
     * @param {Number} offset - normalized offset of the loading bar
     */
    updateProgress: function ( scene, progress, size, offset ) {
        const loadingBar = scene.data.get( "loadingBar" );
        loadingBar.scaleX =
            ( progress * size + offset ) * loadingBar.scaleFactor;
    },
};

export default Loader;
